import React, { KeyboardEvent, useEffect, useId, useRef } from 'react';

import { useIntl } from 'react-intl';
import { Col, Placeholder, Row } from 'react-bootstrap';
import classNames from 'classnames';

import { CalendarIcon, TruckIcon } from 'assets/icons';
import { ShippingSpeed } from 'constants/enums';
import { useAppSelector } from 'hooks';
import { formatDeliveryWindow } from 'utils/Devices';

interface IShippingSpeedSelectorProps {
  /** Both speeds from the preview; undefined until the first answer for this address is in. */
  options: IShippingOption[] | undefined;
  loading: boolean;
  value: ShippingSpeed;
  onChange: (speed: ShippingSpeed) => void;
  /** Bulk flows ship to the practice: different helper, Expedited not selectable. */
  bulk?: boolean;
}

interface ISpeedCopy {
  titleKey: string;
  estimateKey: string;
  noteKey: string;
}

const SPEED_COPY: Record<ShippingSpeed, ISpeedCopy> = {
  [ShippingSpeed.STANDARD]: {
    titleKey: 'devices.shippingSpeed.standard',
    estimateKey: 'devices.shippingSpeed.standardEstimate',
    noteKey: 'devices.shippingSpeed.standardNote'
  },
  [ShippingSpeed.EXPEDITED]: {
    titleKey: 'devices.shippingSpeed.expedited',
    estimateKey: 'devices.shippingSpeed.expeditedEstimate',
    noteKey: 'devices.shippingSpeed.expeditedNote'
  }
};

const SPEEDS = [ShippingSpeed.STANDARD, ShippingSpeed.EXPEDITED];

/**
 * The two shipping-speed cards of the Shipping information step (PHX-1320 / PHX-1321).
 *
 * Presentational only: it is told what the preview said and which speed is chosen. One
 * radiogroup, two radios, roving tab index, arrows to move: a screen reader hears "Standard,
 * radio, 1 of 2, checked", then the estimate and the note, not two clickable boxes.
 */
const ShippingSpeedSelector = ({
  options,
  loading,
  value,
  onChange,
  bulk = false
}: IShippingSpeedSelectorProps) => {
  const intl = useIntl();
  const locale = useAppSelector((state) => state.intl.locale);
  const idPrefix = useId();
  const cardRefs = useRef<Partial<Record<ShippingSpeed, HTMLDivElement | null>>>({});
  const previousValue = useRef(value);

  const titleId = (speed: ShippingSpeed) => `${idPrefix}-${speed}-title`;
  const descriptionId = (speed: ShippingSpeed) => `${idPrefix}-${speed}-description`;

  const optionFor = (speed: ShippingSpeed) => options?.find((option) => option.speed === speed);

  const isUnavailableForAddress = (speed: ShippingSpeed) =>
    speed === ShippingSpeed.EXPEDITED && optionFor(speed)?.available === false;

  const isDisabled = (speed: ShippingSpeed) =>
    speed === ShippingSpeed.EXPEDITED && (bulk || isUnavailableForAddress(speed));

  /**
   * The form resets Expedited to Standard when the preview withdraws it. If that card was the one
   * being used, focus follows the selection instead of staying on a card that can no longer be
   * chosen.
   */
  useEffect(() => {
    const was = previousValue.current;
    previousValue.current = value;
    if (was === value || value !== ShippingSpeed.STANDARD) return;
    const expeditedCard = cardRefs.current[ShippingSpeed.EXPEDITED];
    if (expeditedCard && document.activeElement === expeditedCard && isDisabled(was)) {
      cardRefs.current[ShippingSpeed.STANDARD]?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const select = (speed: ShippingSpeed) => {
    if (!isDisabled(speed) && speed !== value) onChange(speed);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>, speed: ShippingSpeed) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      select(speed);
      return;
    }
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const index = SPEEDS.indexOf(speed);
    const next = SPEEDS[(index + step + SPEEDS.length) % SPEEDS.length];
    if (isDisabled(next)) return;
    select(next);
    cardRefs.current[next]?.focus();
  };

  const renderArrives = (speed: ShippingSpeed) => {
    // Nothing until the address is complete; and an Expedited the address cannot have says only
    // "Not available for this address", never that and a date.
    if ((!loading && !options) || isUnavailableForAddress(speed)) return null;
    const window = optionFor(speed)?.estimatedDelivery;
    const text = window ? formatDeliveryWindow(window.start, window.end, locale) : null;
    return (
      <div
        className="d-flex align-items-center gap-2 mt-2"
        data-test={`shipping-speed-${speed}-arrives`}
      >
        <CalendarIcon className="icon text-secondary flex-shrink-0" style={{ fontSize: '14px' }} />
        {loading ? (
          <Placeholder as="span" animation="glow" className="flex-grow-1">
            <Placeholder xs={8} />
            <span className="visually-hidden">
              {intl.formatMessage({ id: 'devices.shippingSpeed.loadingEstimate' })}
            </span>
          </Placeholder>
        ) : (
          <span className={classNames({ 'text-secondary': !text })}>
            {text
              ? intl.formatMessage({ id: 'devices.shippingSpeed.arrives' }, { window: text })
              : intl.formatMessage({ id: 'devices.shippingSpeed.estimateUnavailable' })}
          </span>
        )}
      </div>
    );
  };

  const renderCard = (speed: ShippingSpeed) => {
    const copy = SPEED_COPY[speed];
    const selected = value === speed;
    const disabled = isDisabled(speed);

    return (
      <Col md={6} key={speed} className="mb-3 mb-md-0">
        <div
          ref={(element) => {
            cardRefs.current[speed] = element;
          }}
          role="radio"
          aria-checked={selected}
          aria-disabled={disabled || undefined}
          aria-labelledby={titleId(speed)}
          aria-describedby={descriptionId(speed)}
          aria-busy={loading || undefined}
          tabIndex={disabled ? -1 : selected ? 0 : -1}
          onClick={() => select(speed)}
          onKeyDown={(event) => handleKeyDown(event, speed)}
          className={classNames('shipping-speed-card rounded-3 p-3 h-100', {
            'shipping-speed-card--selected': selected,
            'shipping-speed-card--disabled': disabled
          })}
          data-test={`shipping-speed-${speed}`}
          data-test-value={selected ? 'selected' : disabled ? 'disabled' : 'available'}
        >
          <div className="d-flex align-items-start justify-content-between gap-2">
            <span id={titleId(speed)} className="fw-semibold">
              {intl.formatMessage({ id: copy.titleKey })}
            </span>
            {speed === ShippingSpeed.STANDARD ? (
              <span className="shipping-speed-badge-included flex-shrink-0">
                {intl.formatMessage({ id: 'includedKey' })}
              </span>
            ) : (
              <span className="fw-semibold small text-end flex-shrink-0">
                {intl.formatMessage({ id: 'devices.shippingSpeed.additionalFees' })}
              </span>
            )}
          </div>
          <div id={descriptionId(speed)}>
            <div className="mt-2">{intl.formatMessage({ id: copy.estimateKey })}</div>
            {renderArrives(speed)}
            <div className="text-secondary small mt-2">
              {intl.formatMessage({ id: copy.noteKey })}
            </div>
            {isUnavailableForAddress(speed) && (
              <div
                className="fw-semibold small mt-2"
                data-test="shipping-speed-expedited-unavailable"
              >
                {intl.formatMessage({ id: 'devices.shippingSpeed.notAvailableForAddress' })}
              </div>
            )}
          </div>
        </div>
      </Col>
    );
  };

  return (
    <div className="mt-5" data-test="shipping-speed-section">
      <h6 className="mb-2 d-flex align-items-center">
        <TruckIcon className="icon me-2" style={{ fontSize: '18px' }} />
        {intl.formatMessage({ id: 'devices.shippingSpeed.title' }).toUpperCase()}
      </h6>
      <p className="text-secondary shipping-speed-helper mb-3">
        {intl.formatMessage({
          id: bulk ? 'devices.shippingSpeed.helperBulk' : 'devices.shippingSpeed.helper'
        })}
      </p>
      <Row
        role="radiogroup"
        aria-label={intl.formatMessage({ id: 'devices.shippingSpeed.title' })}
        className="row-eq-height"
      >
        {SPEEDS.map(renderCard)}
      </Row>
    </div>
  );
};

export default ShippingSpeedSelector;
